import { useCallback, useState } from "react";
import AccountMpProvider from "@/core/data-provider/data-mp/account/account-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useErrorTranslation from "@/core/helpers/useErrorTranslation";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { setActiveSessions } from "@/domains/account/slice";

export default function useActiveSessions() {
  const dispatch = useAppDispatch();
  const tError = useErrorTranslation();

  const [getSessionsIsPending, setGetSessionsIsPending] = useState(false);
  const [getSessionsIsCompleted, setGetSessionsIsCompleted] = useState<null | boolean>(null);
  const [getSessionsError, setGetSessionsError] = useState<string>("");
  const [closeSessionsIsPending, setCloseSessionsIsPending] = useState(false);
  const [closeSessionsIsCompleted, setCloseSessionsIsCompleted] = useState<null | boolean>(null);
  const [closeSessionsError, setCloseSessionsError] = useState<string>("");

  const getSessions = useCallback(async () => {
    try {
      setGetSessionsIsPending(true);
      const response = await AccountMpProvider.getAllActiveSessions();

      if (response instanceof RpcMpProviderSuccessResponse) {
        dispatch(setActiveSessions(response.data.sessions));
        setGetSessionsIsCompleted(true);
      } else {
        setGetSessionsIsCompleted(false);
      }
    } catch ({ message }) {
      setGetSessionsError(tError(message));
      setGetSessionsIsCompleted(false);
    } finally {
      setGetSessionsIsPending(false);
    }
  }, [dispatch]);

  const closeSessions = useCallback(async () => {
    try {
      setCloseSessionsIsPending(true);
      const settingsResponse = await AccountMpProvider.closeAllActiveSessions();

      if (settingsResponse instanceof RpcMpProviderSuccessResponse) {
        setCloseSessionsIsCompleted(true);
      } else {
        setCloseSessionsIsCompleted(false);
      }
    } catch ({ message }) {
      setCloseSessionsError(tError(message));
      setCloseSessionsIsCompleted(false);
    } finally {
      setCloseSessionsIsPending(false);
    }
  }, []);

  return {
    getSessionsIsPending,
    setGetSessionsIsPending,
    getSessionsError,
    setGetSessionsError,
    closeSessionsIsPending,
    setCloseSessionsIsPending,
    closeSessionsError,
    setCloseSessionsError,
    getSessions,
    closeSessions,
    getSessionsIsCompleted,
    closeSessionsIsCompleted,
  };
}
